import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Grid, Paper, Alert,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'parties', title: 'Parties & Services' },
  { id: 'terms', title: 'Terms & Dates' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  tutorId: string;
  clientId: string;
  serviceAuthorisationId: string;
  services: Array<{ serviceId: string; rate: number }>;
  effectiveFrom: string;
  effectiveTo: string;
  description: string;
}

const INITIAL_VALUES: FormValues = {
  tutorId: '',
  clientId: '',
  serviceAuthorisationId: '',
  services: [],
  effectiveFrom: '',
  effectiveTo: '',
  description: '',
};

const stepDescriptions: Record<string, string> = {
  parties: "Choose the tutor, client, and service for this agreement. The agreement defines the tutoring relationship.",
  terms: "Set the agreement start/end date, session frequency, and billing terms.",
  review: "Review the agreement details before creating. The agreement will be activated via workflow.",
};

export const TutoringAgreementWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-agreements', {
      data: {
        tutorId: v.tutorId,
        clientId: v.clientId,
        serviceAuthorisationId: v.serviceAuthorisationId,
        services: v.services,
        effectiveFrom: v.effectiveFrom,
        effectiveTo: v.effectiveTo,
        description: v.description,
        status: 'Draft',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('tutoring-agreement', 'draft', ['ACTIVATE']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'tutoring-agreement',
        entityId,
        entityName: `Agreement ${v.effectiveFrom} - ${v.effectiveTo}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate } = useRuleEvaluation({
    ruleId: 'tutoring-agreement-guards',
    context: values as unknown as Record<string, unknown>,
  });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'parties':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Tutor ID" value={vals.tutorId} onChange={e => updateField('tutorId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Client ID" value={vals.clientId} onChange={e => updateField('clientId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Service Authorisation ID" value={vals.serviceAuthorisationId} onChange={e => updateField('serviceAuthorisationId', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'terms':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Effective From" type="date" value={vals.effectiveFrom} onChange={e => updateField('effectiveFrom', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Effective To" type="date" value={vals.effectiveTo} onChange={e => updateField('effectiveTo', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Description" value={vals.description} onChange={e => updateField('description', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review agreement details before creating. A workflow will be initialized in Draft state.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Agreement Info</Typography>
                <Typography variant="body2"><strong>Tutor:</strong> {vals.tutorId}</Typography>
                <Typography variant="body2"><strong>Client:</strong> {vals.clientId}</Typography>
                <Typography variant="body2"><strong>Period:</strong> {vals.effectiveFrom} to {vals.effectiveTo}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Tutoring Agreement Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Tutor', value: vals.tutorId },
            { label: 'Client', value: vals.clientId },
            { label: 'Period', value: `${vals.effectiveFrom} to ${vals.effectiveTo}` },
          ]}
          footerActions={[
            { label: 'View Agreements', to: '/tutoring-agreements' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
